import { Link, useParams } from "@tanstack/react-router";
import { Eye } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { ErrorBlock, Img, Price, Spinner } from "../../components/ui.tsx";
import { useCaseDetail } from "../../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 案例详情
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **「这套用到的建材」要跟着图一起看得见，所以钉在右边。**
 *    手机版是图看完了才滚到商品清单 —— 用户看着一张客厅图，
 *    想知道"那个地板多少钱"，得往下翻过整段正文和一串大图。
 *    网页上把清单固定在右栏：**看哪张图，就顺手看得到用了什么。**
 *    这一页的目的本来就是内容带货，隔开就不带货了。
 *
 * 🔴 **里面的商品卡片没有静态价格。** 价格是服务端按当前访问者的归属链
 *    算出来的 —— 同一篇案例，挂在不同分销商名下的人看到的是两个数。
 *    所以这一页不能缓存到 CDN，也不能把价格随内容一起发布。
 */
export function DesktopCaseDetail() {
  const { slug } = useParams({ from: "/cases/$slug" });
  const { data, isPending, error, refetch } = useCaseDetail(slug);

  if (isPending)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );
  if (error)
    return (
      <DesktopShell>
        <ErrorBlock error={error} onRetry={() => refetch()} />
      </DesktopShell>
    );

  return (
    <DesktopShell>
      <Crumbs
        items={[
          { label: "首页", to: "/" },
          { label: "装修案例", to: "/cases" },
          { label: data.title },
        ]}
      />

      <div className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-8">
        {/* ══════════ 左：内容 ══════════ */}
        <article className="min-w-0">
          <h1 className="text-[26px] leading-snug font-medium">{data.title}</h1>
          <p className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[14px] text-muted-foreground">
            {data.style && <span>{data.style}</span>}
            {data.areaSqm !== null && <span>{data.areaSqm} ㎡</span>}
            {data.budget !== null && (
              <span>
                预算 <Price cents={data.budget} />
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="size-3.5" />
              {data.viewCount}
            </span>
          </p>

          <Img
            src={data.coverUrl}
            alt={data.title}
            className="mt-5 aspect-[16/10] w-full rounded-xl object-cover"
          />

          {data.contentMd && (
            /*
              ⚠️ 不渲染 HTML —— 内容是后台写的纯文本，按原样保留换行就够，
                 上 markdown 渲染器等于给自己开一个 XSS 面。
              ⚠️ 正文限宽 66 个字符：1280 宽一行排下来眼睛会跟丢行。
            */
            <p className="mt-6 max-w-[66ch] text-[15px] leading-[1.9] whitespace-pre-wrap text-muted-foreground">
              {data.contentMd}
            </p>
          )}

          {data.imageUrls.length > 0 && (
            <div className="mt-6 space-y-4">
              {data.imageUrls.map((url) => (
                <Img key={url} src={url} alt="" className="w-full rounded-xl" />
              ))}
            </div>
          )}
        </article>

        {/* ══════════ 右：这套用到的建材（常驻） ══════════ */}
        {data.products.length > 0 && (
          <aside className="sticky top-24 overflow-hidden rounded-xl border border-border bg-card">
            <h2 className="border-b border-border bg-secondary/40 px-5 py-3 text-[14px] font-medium">
              这套用到的建材
            </h2>
            <div className="max-h-[70vh] overflow-y-auto">
              {data.products.map((p) => (
                <Link
                  key={p.id}
                  to="/products/$id"
                  params={{ id: String(p.id) }}
                  className="flex items-center gap-3 border-b border-border px-5 py-3.5 transition-colors last:border-b-0 hover:bg-secondary/50"
                >
                  <Img src={p.imageUrl} alt="" className="size-14 shrink-0 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-[14px] leading-snug">{p.title}</p>
                    <p className="mt-1">
                      <Price cents={p.fromPrice} className="text-[15px] text-brand-600" />
                      <span className="ml-1 text-[12px] text-muted-foreground">起</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>
    </DesktopShell>
  );
}
